import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2, LinkedinIcon } from 'lucide-react';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

interface ContentBlock {
  type: string;
  text?: string;
  items?: string[] | { date: string; event: string }[];
}

interface Article {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  content: ContentBlock[];
}

function renderContent(block: ContentBlock, index: number) {
  switch (block.type) {
    case 'heading2':
      return (
        <h2 key={index} className="text-2xl font-bold text-eco-green-900 mt-8 mb-4">
          {block.text}
        </h2>
      );
    case 'heading3':
      return (
        <h3 key={index} className="text-xl font-bold text-eco-green-800 mt-6 mb-3">
          {block.text}
        </h3>
      );
    case 'paragraph':
      return (
        <p key={index} className="text-gray-700 mb-4 leading-relaxed">
          {block.text}
        </p>
      );
    case 'list':
      return (
        <ul key={index} className="list-disc list-inside space-y-2 mb-4 text-gray-700">
          {(block.items as string[])?.map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );
    case 'timeline':
      return (
        <div key={index} className="my-6 space-y-3">
          {(block.items as { date: string; event: string }[])?.map((item, i) => (
            <div key={i} className="flex gap-4 p-4 bg-eco-green-50 rounded-xl">
              <span className="font-semibold text-eco-green-700 whitespace-nowrap min-w-[140px]">
                {item.date}
              </span>
              <span className="text-gray-700">{item.event}</span>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export function InsightDetail() {
  const { insightId } = useParams<{ insightId: string }>();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  // SSR-safe animation props
  const heroAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const imageAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const contentAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const ctaAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );

  useEffect(() => {
    fetch('/data/insights.json')
      .then((res) => res.json())
      .then((data) => {
        setArticles(data.articles);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load insights:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="pt-32 pb-16 min-h-screen">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto animate-pulse space-y-4">
              <div className="h-8 bg-eco-green-100 rounded w-1/4" />
              <div className="h-12 bg-eco-green-100 rounded w-3/4" />
              <div className="h-64 bg-eco-green-100 rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const article = articles.find((a) => a.id === insightId);

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const currentIndex = articles.findIndex((a) => a.id === insightId);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{article.title} | Eco Enviro Insights</title>
        <meta name="title" content={`${article.title} | Eco Enviro Insights`} />
        <meta name="description" content={article.excerpt} />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/insights/${article.id}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${SITE_URL}/insights/${article.id}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}${article.image}`} />
        <meta property="og:locale" content="en_IN" />
        <meta property="article:published_time" content={article.date} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={`${SITE_URL}${article.image}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-8 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            className="max-w-4xl mx-auto"
          >
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-eco-green-600 hover:text-eco-green-700 mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>

            <span className="inline-block px-3 py-1 bg-eco-green-600 text-white text-sm font-semibold rounded-full mb-4">
              {article.category}
            </span>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-eco-green-900 mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-8">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(article.date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {article.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={imageAnim.initial}
            animate={imageAnim.animate}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={contentAnim.initial}
            animate={contentAnim.animate}
            transition={{ delay: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <div className="prose prose-lg prose-green max-w-none">
              {article.content.map((block, index) => renderContent(block, index))}
            </div>

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-gray-600 font-medium">Share this article:</span>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://theecoenviro.com/insights/${article.id}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-eco-green-100 text-eco-green-700 rounded-lg hover:bg-eco-green-200 transition-colors"
                  >
                    <LinkedinIcon className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
                    aria-label="Share this article"
                    className="p-2 bg-eco-green-100 text-eco-green-700 rounded-lg hover:bg-eco-green-200 transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              {prevArticle && (
                <Link
                  to={`/insights/${prevArticle.id}`}
                  className="p-4 bg-eco-green-50 rounded-xl hover:bg-eco-green-100 transition-colors group"
                >
                  <span className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                    <ArrowLeft className="w-4 h-4" />
                    Previous Article
                  </span>
                  <span className="font-semibold text-eco-green-900 group-hover:text-eco-green-700">
                    {prevArticle.title}
                  </span>
                </Link>
              )}
              {nextArticle && (
                <Link
                  to={`/insights/${nextArticle.id}`}
                  className="p-4 bg-eco-green-50 rounded-xl hover:bg-eco-green-100 transition-colors group sm:text-right"
                >
                  <span className="text-sm text-gray-500 flex items-center gap-1 mb-2 sm:justify-end">
                    Next Article
                    <ArrowRight className="w-4 h-4" />
                  </span>
                  <span className="font-semibold text-eco-green-900 group-hover:text-eco-green-700">
                    {nextArticle.title}
                  </span>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-eco-green-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={ctaAnim.initial}
            whileInView={ctaAnim.animate}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Need Help with Waste Management?
            </h2>
            <p className="text-eco-green-100 mb-8">
              Our experts can help you develop a customized waste management strategy for your business.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-eco-green-700 font-semibold rounded-xl hover:bg-eco-green-50 transition-colors"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
